import type { AdvancedCreateFromFileSettings } from '../types';
import FetchPromise from '~/agent/fetch-promise';
import { useMultiStepFileUpload } from '~/hooks';
import { useDefaultMutation } from '~/lib/react-query';
import { hideLoader, showLoader } from '~/common';
import { DEFAULT_ADVANCED_SETTINGS } from '../components/constants';
import { useCreateSourceDocumentMutation } from './createSourceDocument';
import { useUpdateSourceDocumentMutation } from './updateSourceDocument';

export interface CreateGuideWithSourceDocumentsParams {
  sourceDocumentIds: string[];
  title: string;
  itemFolderId?: string;
  advancedSettings?: AdvancedCreateFromFileSettings;
}

export interface CreateGuideFromFileParams {
  files: File[];
  title: string;
  itemFolderId?: string;
  advancedSettings?: AdvancedCreateFromFileSettings;
}

export function createGuideWithSourceDocuments({
  title,
  sourceDocumentIds,
  itemFolderId,
  advancedSettings = DEFAULT_ADVANCED_SETTINGS,
}: CreateGuideWithSourceDocumentsParams): Promise<{ id: string }> {
  const data: {
    title: string;
    item_folder_id?: string;
    ai_generation: {
      source_document_ids: string[];
      guide_detail?: number;
      interactivity?: number;
    };
  } = {
    title,
    ai_generation: {
      source_document_ids: sourceDocumentIds,
      guide_detail: advancedSettings.guideDetail,
      interactivity: advancedSettings.interactivity,
    },
  };

  if (itemFolderId) data.item_folder_id = itemFolderId;

  return FetchPromise.post(`courses/ai_generations`, data);
}

export function useCreateGuideFromFileMutation() {
  const createSourceDocumentMutation = useCreateSourceDocumentMutation();
  const updateSourceDocumentMutation = useUpdateSourceDocumentMutation();

  const uploader = useMultiStepFileUpload({
    onCreateMetadata: createSourceDocumentMutation.mutateAsync,
    onUpdateMetadata: updateSourceDocumentMutation.mutateAsync,
  });

  return useDefaultMutation(async ({ files, title, itemFolderId, advancedSettings }: CreateGuideFromFileParams) => {
    showLoader(false, false, '&nbsp;&nbsp;Extracting content...', true);
    const results = await Promise.all(files.map((f) => uploader.mutateAsync(f)));
    hideLoader();

    return createGuideWithSourceDocuments({
      title,
      itemFolderId,
      sourceDocumentIds: results.map((result) => result.id),
      advancedSettings,
    });
  });
}
